// Content for the Discord LFG bot landing (/discord-lfg-bot).
// Facts are sourced from the bot itself (teamplay-discord CommandsConfig / LfgHelpService)
// and the discord-lfg-admin feature — keep them in sync when the bot changes.

export const BOT_INVITE_URL = 'https://discord.com/oauth2/authorize?client_id=1294303982243549194';
export const SUPPORT_DISCORD_URL = 'https://discord.gg/zHe9vqGb7Z';
export const TOP_GG_VOTE_URL = 'https://top.gg/bot/1294303982243549194/vote';
export const DISCORD_BOT_LIST_URL = 'https://discordbotlist.com/bots/teamplaylfg';
export const SUPPORT_EMAIL = 'support@teamplay.gg';

// Real LFG banner cards generated by the bot (same assets as the main landing's LfgBanners).
export const BOT_BANNERS = [
  'https://media.teamplay.gg/assets/FdaB/xsY.png',
  'https://media.teamplay.gg/assets/FdaB/BiJ.png',
  'https://media.teamplay.gg/assets/FdaB/zt0.png',
];

export const STATS = [
  { value: 120, suffix: '+', label: 'Active servers' },
  { value: 14, suffix: 'K+', label: 'Gamers connected' },
  { value: 8, suffix: 'K+', label: 'LFG posts created' },
  { value: 18, suffix: '+', label: 'Games supported' },
];

export type Feature = {
  icon: string;
  title: string;
  description: string;
};

export const FEATURES: Feature[] = [
  {
    icon: '🎯',
    title: 'Structured LFG posts',
    description:
      'A guided wizard collects game, platform, mic and squad size, then posts a rich LFG card. Others join with one click – no "anyone wanna play?" lost in chat.',
  },
  {
    icon: '⏰',
    title: 'Play now, schedule, or anytime',
    description:
      'Start a session right away, schedule it for later with automatic DM reminders, or leave an open "anytime" post that keeps recruiting for you.',
  },
  {
    icon: '🎙️',
    title: 'Auto voice channels',
    description:
      'A temporary voice room spins up for each session and cleans itself up afterwards. Your squad gets a "Join voice" button right on the card.',
  },
  {
    icon: '🌐',
    title: 'Cross-server discovery',
    description:
      'Posts created on teamplay.gg or in any connected server appear everywhere the bot is installed – squads fill faster than any single community could manage.',
  },
  {
    icon: '🔔',
    title: 'Smart DM notifications',
    description:
      'Members get personal messages when someone joins their squad, a scheduled session is about to start, or they receive an invite – accept or decline in one tap.',
  },
  {
    icon: '📊',
    title: 'Web dashboard & stats',
    description:
      'Manage channel-to-game mappings from a web admin dashboard, and track LFG activity per game and channel with /lfg_stats.',
  },
];

export type Command = {
  cmd: string;
  description: string;
  adminOnly?: boolean;
};

// Mirrors teamplay-discord CommandsConfig — the actual slash commands the bot registers.
export const COMMANDS: Command[] = [
  { cmd: '/lfg_create', description: 'Create an LFG post to find teammates' },
  { cmd: '/lfg_discover', description: 'Browse open LFG posts by game and join' },
  { cmd: '/lfg_manage', description: 'View and manage your active LFG posts' },
  {
    cmd: '/lfg_notifications',
    description: 'Map channels to games for LFG posts',
    adminOnly: true,
  },
  { cmd: '/lfg_stats', description: 'See LFG activity stats for your server', adminOnly: true },
  { cmd: '/lfg_help', description: 'Learn about Teamplay LFG features' },
];

export type Step = {
  title: string;
  description: string;
};

export const STEPS: Step[] = [
  {
    title: 'Invite the bot',
    description:
      'Click "Add to Discord" and pick your server. The bot is in – no setup wizard, no config files.',
  },
  {
    title: 'Pick games & channels',
    description:
      'Run /lfg_notifications or open the web dashboard to choose which channels receive LFG posts for which games.',
  },
  {
    title: 'Members squad up',
    description:
      'Anyone can create LFG posts, browse open sessions and jump into voice together – 24/7, without pinging a mod.',
  },
];

export type GameEntry = {
  name: string;
  slug: string;
};

// Supported games, each linking to its Teamplay landing page. Kept static so the SEO
// links render without a query; when a game (and its landing route) ships, add it here.
export const GAMES: GameEntry[] = [
  { name: 'Apex Legends', slug: 'apex-legends' },
  { name: 'ARC Raiders', slug: 'arc-raiders' },
  { name: 'Battlefield 6', slug: 'battlefield6' },
  { name: 'Call of Duty: Warzone', slug: 'call-of-duty-warzone-2' },
  { name: 'Counter-Strike 2', slug: 'cs2' },
  { name: 'Deadlock', slug: 'deadlock' },
  { name: 'Destiny 2', slug: 'destiny-2' },
  { name: 'Dota 2', slug: 'dota-2' },
  { name: 'Fortnite', slug: 'fortnite' },
  { name: 'GTA Online', slug: 'gta-5' },
  { name: 'League of Legends', slug: 'league-of-legends' },
  { name: 'Marathon', slug: 'marathon' },
  { name: 'Marvel Rivals', slug: 'marvel-rivals' },
  { name: 'Mobile Legends: Bang Bang', slug: 'mobile-legends' },
  { name: 'Overwatch 2', slug: 'overwatch' },
  { name: 'PUBG', slug: 'pubg' },
  { name: 'Rainbow Six Siege', slug: 'rainbow-six-siege' },
  { name: 'The Finals', slug: 'the-finals' },
  { name: 'Valorant', slug: 'valorant' },
  { name: 'WARDOGS', slug: 'wardogs' },
  { name: 'World of Warcraft', slug: 'wow' },
];

export type PricingTier = {
  badge: string;
  title: string;
  description: string;
  details: string;
  cta: { label: string; href: string } | null;
  highlighted?: boolean;
};

export const PRICING: PricingTier[] = [
  {
    badge: 'Most popular',
    title: 'Free forever',
    description:
      'All slash commands, cross-server discovery, auto voice channels and the web admin dashboard.',
    details:
      'Includes Discord + email support. Perfect for communities of any size that want structured matchmaking at zero cost.',
    cta: { label: 'Add to Discord', href: BOT_INVITE_URL },
    highlighted: true,
  },
  {
    badge: 'Early access',
    title: 'Large communities',
    description: 'For servers with 10,000+ members who want deeper integration.',
    details:
      'Dedicated voice channels in your own server, priority support, and early access to Pro features. Free during the pilot.',
    cta: {
      label: 'Send request',
      href: `mailto:${SUPPORT_EMAIL}?subject=Teamplay LFG - Large community request`,
    },
  },
  {
    badge: 'Coming soon',
    title: 'Pro',
    description:
      'Analytics, dedicated voice hosting, and premium tools for esports orgs & networks.',
    details:
      'Track LFG usage, session success rates and member retention with advanced dashboards.',
    cta: null,
  },
];

export type FaqEntry = {
  question: string;
  answer: string;
};

export const BOT_FAQ: FaqEntry[] = [
  {
    question: 'What does the Teamplay LFG bot do?',
    answer:
      'It turns your Discord server into an LFG hub. Members post what they want to play – right now, at a scheduled time, or anytime – and others join with one click. Posts also sync with teamplay.gg and every other connected server, so squads fill fast instead of "anyone wanna play?" getting buried in chat.',
  },
  {
    question: 'How do I add the bot to my Discord server?',
    answer:
      'Click "Add to Discord", authorize the bot, and map your channels to games with /lfg_notifications or the web admin dashboard. You are live in about two minutes.',
  },
  {
    question: 'Is the Teamplay LFG bot free?',
    answer:
      'Yes. All slash commands, cross-server discovery, auto voice channels and the admin dashboard are free forever. Optional paid tiers exist only for very large communities that want dedicated voice hosting and advanced analytics.',
  },
  {
    question: 'Which games does the bot support?',
    answer:
      'Apex Legends, Valorant, Counter-Strike 2, Fortnite, Call of Duty: Warzone, League of Legends, Dota 2, Overwatch 2, Marvel Rivals and more – 18 games and counting. The list comes straight from the teamplay.gg catalog and grows regularly; you can request new titles any time.',
  },
  {
    question: 'Do members need a Teamplay account?',
    answer:
      'Creating and joining LFG posts uses a free Teamplay.gg account, created in one click: the bot shows a Confirm button, the member signs in with Discord, and they are sent right back to the channel. That link is what powers cross-server discovery and DM notifications.',
  },
  {
    question: 'How do voice channels work?',
    answer:
      'When a session forms, the bot creates a temporary voice channel for the squad and adds a "Join voice" button to the session card. The room is cleaned up automatically after the session, so your channel list stays tidy.',
  },
  {
    question: 'How do scheduled sessions and reminders work?',
    answer:
      'Members can schedule a session for a specific date and time. Everyone who joined gets a DM reminder before the start, plus notifications when someone joins, leaves, or invites them – no one misses the party.',
  },
  {
    question: 'Is my data safe?',
    answer:
      'The bot only uses the Discord data it needs to match players and post LFG messages. Read the Privacy Policy and Terms of Use for the full details.',
  },
];
